import { createApp } from "vue";
import { createPinia } from "pinia";
import { createPersistedState } from "pinia-plugin-persistedstate";
import { createI18n } from "vue-i18n";
import { Vue3ProgressPlugin } from "@marcoschulte/vue3-progress";
import { PerfectScrollbar } from "vue3-perfect-scrollbar";
import Toast, { POSITION } from "vue-toastification";
import type { PluginOptions } from "vue-toastification";
import mitt from "mitt";
import { IonicVue } from "@ionic/vue";
import { addIcons } from "ionicons";
import { registerLicense } from "@syncfusion/ej2-base";

import App from "./App.vue";
import router from "./router";
import { messages } from "./lang/traductions";

import Panel from "@/components/bootstrap/Panel.vue";
import PanelBody from "@/components/bootstrap/PanelBody.vue";
import PanelHeader from "@/components/bootstrap/PanelHeader.vue";
import PanelTitle from "@/components/bootstrap/PanelTitle.vue";
import PanelFooter from "@/components/bootstrap/PanelFooter.vue";
import PanelToolbar from "@/components/bootstrap/PanelToolbar.vue";

import { useAppOptionStore } from "@/stores/app-option";

// ============================================================================
// 🎨 Estilos globales
// ============================================================================

import "bootstrap/dist/css/bootstrap.min.css";

import "vue3-perfect-scrollbar/style.css";
import "@marcoschulte/vue3-progress/dist/index.css";
import "vue-toastification/dist/index.css";

import "@fortawesome/fontawesome-free/scss/fontawesome.scss";
import "@fortawesome/fontawesome-free/scss/regular.scss";
import "@fortawesome/fontawesome-free/scss/solid.scss";
import "@fortawesome/fontawesome-free/scss/brands.scss";
import "@fortawesome/fontawesome-free/scss/v4-shims.scss";

import "@ionic/vue/css/core.css";
import "@ionic/vue/css/normalize.css";
import "@ionic/vue/css/structure.css";
import "@ionic/vue/css/typography.css";
import "@ionic/vue/css/padding.css";
import "@ionic/vue/css/float-elements.css";
import "@ionic/vue/css/text-alignment.css";
import "@ionic/vue/css/text-transformation.css";
import "@ionic/vue/css/flex-utils.css";
import "@ionic/vue/css/display.css";

import "./scss/vue.scss";

import "bootstrap/dist/js/bootstrap.bundle.min.js";

// ============================================================================
// 🧪 Mocks locales
// ============================================================================

if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_MOCKS === "true") {
  import("@/stores/mocks/management/setupManagementMocks");
}

// ============================================================================
// 🔷 Iconos Ionic
// ============================================================================

import {
  addOutline,
  alertCircleOutline,
  arrowBackOutline,
  calendar,
  calendarOutline,
  checkmarkDoneOutline,
  closeOutline,
  listOutline,
  pauseOutline,
  playOutline,
  reload,
  returnUpBackOutline,
  search,
  stopOutline,
  todayOutline,
  walkOutline,
} from "ionicons/icons";

addIcons({
  "add-outline": addOutline,
  "alert-circle-outline": alertCircleOutline,
  "arrow-back-outline": arrowBackOutline,
  "calendar-outline": calendarOutline,
  "checkmark-done-outline": checkmarkDoneOutline,
  "close-outline": closeOutline,
  "list-outline": listOutline,
  "pause-outline": pauseOutline,
  "play-outline": playOutline,
  "refresh-outline": reload,
  "return-up-back-outline": returnUpBackOutline,
  "stop-outline": stopOutline,
  "today-outline": todayOutline,
  "walk-outline": walkOutline,
  calendar,
  search,
});

// ============================================================================
// 🔧 PWA principal
// ============================================================================

declare const __APP_VERSION__: string;
declare const __SEMVER__: string;

if (import.meta.env.PROD && "serviceWorker" in navigator) {
  const ssGet = (key: string): string | null => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const ssSet = (key: string, value: string | null): void => {
    try {
      if (value === null) {
        sessionStorage.removeItem(key);
      } else {
        sessionStorage.setItem(key, value);
      }
    } catch {
      // Evita errores en navegadores restrictivos.
    }
  };

  const hardReload = (): void => {
    window.location.href = window.location.href;
  };

  window.addEventListener("load", async () => {
    const swPath = "/sw.js";
    const scope = "/";

    const FLAG_UPDATING = "main:pwa:updating";
    const FLAG_JUST_UPDATED = "main:pwa:just-updated";
    const FLAG_BLOCK_AUTO = "main:pwa:block-auto";
    const LAST_VERSION_KEY = "main:pwa:last-version";

    try {
      const previousVersion = localStorage.getItem(LAST_VERSION_KEY);
      const wasUpdating = ssGet(FLAG_UPDATING) === "1";
      const isBlocked = ssGet(FLAG_BLOCK_AUTO) === "1";

      if (
        wasUpdating &&
        !isBlocked &&
        previousVersion &&
        previousVersion !== __APP_VERSION__
      ) {
        ssSet(FLAG_UPDATING, null);
        ssSet(FLAG_JUST_UPDATED, "1");
      }

      localStorage.setItem(LAST_VERSION_KEY, __APP_VERSION__);
    } catch {
      // No bloqueamos el arranque por errores de storage.
    }

    try {
      const registration = await navigator.serviceWorker.register(swPath, {
        scope,
        type: "module",
        updateViaCache: "none",
      });

      const dispatchNeedRefresh = (): void => {
        window.dispatchEvent(new CustomEvent("pwa:need-refresh"));
      };

      const attachInstallingListener = (worker: ServiceWorker): void => {
        worker.addEventListener("statechange", () => {
          if (
            worker.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            dispatchNeedRefresh();
          }
        });
      };

      (window as any).__pwa = {
        flags: {
          updating: FLAG_UPDATING,
          justUpdated: FLAG_JUST_UPDATED,
          blockAuto: FLAG_BLOCK_AUTO,
        },

        checkNow: async () => {
          await registration.update();

          if (registration.waiting) {
            dispatchNeedRefresh();
            return;
          }

          if (registration.installing) {
            attachInstallingListener(registration.installing);
          }
        },

        applyUpdateNow: async () => {
          ssSet(FLAG_BLOCK_AUTO, null);
          ssSet(FLAG_UPDATING, "1");
          ssSet(FLAG_JUST_UPDATED, null);

          if (registration.waiting) {
            registration.waiting.postMessage({ type: "SKIP_WAITING" });
          } else {
            try {
              await registration.update();
            } catch {
              // Best effort.
            }

            setTimeout(() => {
              registration.waiting?.postMessage({ type: "SKIP_WAITING" });
            }, 150);
          }

          setTimeout(() => {
            if (ssGet(FLAG_UPDATING) === "1") {
              hardReload();
            }
          }, 1500);
        },

        hardReload,

        get version() {
          return __APP_VERSION__;
        },

        get semver() {
          return __SEMVER__;
        },
      };

      window.dispatchEvent(new CustomEvent("pwa:ready"));

      if (registration.waiting) {
        dispatchNeedRefresh();
      }

      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;

        if (!newWorker) return;

        newWorker.addEventListener("statechange", () => {
          if (
            newWorker.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            dispatchNeedRefresh();
          }
        });
      });

      if (!navigator.serviceWorker.controller) {
        window.dispatchEvent(new CustomEvent("pwa:offline-ready"));
      }

      let reloading = false;

      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (reloading) return;

        const isBlocked = ssGet(FLAG_BLOCK_AUTO) === "1";
        const isUpdating = ssGet(FLAG_UPDATING) === "1";

        if (isBlocked || !isUpdating) return;

        reloading = true;

        ssSet(FLAG_UPDATING, null);
        ssSet(FLAG_JUST_UPDATED, "1");

        hardReload();
      });
    } catch (error) {
      console.error("Error al registrar el Service Worker:", error);
    }
  });

  let pwaCheckInterval: ReturnType<typeof setInterval> | undefined;

  const startPwaAutoCheck = (): void => {
    if (pwaCheckInterval !== undefined) return;

    (window as any).__pwa?.checkNow?.();

    pwaCheckInterval = setInterval(
      () => {
        (window as any).__pwa?.checkNow?.();
      },
      60 * 60 * 1000,
    );
  };

  const stopPwaAutoCheck = (): void => {
    if (pwaCheckInterval === undefined) return;

    clearInterval(pwaCheckInterval);
    pwaCheckInterval = undefined;
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      startPwaAutoCheck();
    } else {
      stopPwaAutoCheck();
    }
  });

  window.addEventListener("focus", () => {
    (window as any).__pwa?.checkNow?.();
  });

  window.addEventListener("beforeunload", stopPwaAutoCheck);

  if (document.visibilityState === "visible") {
    startPwaAutoCheck();
  }
}

// ============================================================================
// 🔔 Toast
// ============================================================================

const toastOptions: PluginOptions = {
  position: POSITION.TOP_LEFT,
  timeout: 5000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: "button",
  icon: true,
  rtl: false,
  transition: "Vue-Toastification__slideBlurred",
  maxToasts: 20,
  newestOnTop: true,
};

// ============================================================================
// 🌍 i18n
// ============================================================================

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  allowComposition: true,
  locale: "es",
  fallbackLocale: "es",
  messages,
});

// ============================================================================
// 🧩 App
// ============================================================================

registerLicense(
  "Ngo9BigBOggjHTQxAR8/V1NBaF5cXmZCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdnWXteeHRTRWJYVkJyW0A=",
);

const emitter = mitt();
const app = createApp(App);
const pinia = createPinia();

pinia.use(createPersistedState());

app.use(pinia);
app.use(router);
app.use(Vue3ProgressPlugin);
app.use(i18n);
app.use(IonicVue);
app.use(Toast, toastOptions);

app.component("Panel", Panel);
app.component("PanelBody", PanelBody);
app.component("PanelHeader", PanelHeader);
app.component("PanelFooter", PanelFooter);
app.component("PanelToolbar", PanelToolbar);
app.component("PanelTitle", PanelTitle);
app.component("PerfectScrollbar", PerfectScrollbar);

app.config.globalProperties.emitter = emitter;

// ============================================================================
// 💾 Persistencia al cerrar/refrescar
// ============================================================================

const appOption = useAppOptionStore();

window.addEventListener("beforeunload", () => {
  const path = window.location.pathname || "/";
  const fullPath = path + window.location.search + window.location.hash;

  if (path !== "/" && path !== "/login") {
    localStorage.setItem("lastPath", fullPath);
  } else {
    localStorage.removeItem("lastPath");
  }

  localStorage.setItem("selectedMenu", appOption.appSelectedMenu);
  localStorage.setItem("selectedMenuIcon", appOption.appSelectedMenuIcon);

  if (appOption.appCompany !== "" && appOption.appApiURL !== "") {
    localStorage.setItem("company", appOption.appCompany);
    localStorage.setItem("apiURL", appOption.appApiURL);
  }

  const rememberSetup = JSON.parse(
    localStorage.getItem("rememberSetup") || "false",
  );

  if (rememberSetup) {
    localStorage.setItem("reloadDetected", "true");
  } else {
    localStorage.removeItem("reloadDetected");
  }
});

app.mount("#app");
